import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, FileText, Check, Trash2, Edit, Download, Clock, AlertCircle, UserPlus, Eye, X, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { handleMobileInput, handleGSTInput } from '@/utils/inputValidation';
import { determineGstType, getEntityState, getEntityGstin } from '@/utils/gstUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: determine GST type based on client's GSTIN or state name
const getClientGstType = (gstNumber, stateName) => determineGstType(gstNumber, stateName);

const BILLING_CYCLES = [
  { key: 'monthly',     label: '1 Month'   },
  { key: 'quarterly',   label: '3 Months'  },
  { key: 'half_yearly', label: '6 Months'  },
  { key: 'yearly',      label: '12 Months' },
  { key: 'two_years',   label: '24 Months' },
  { key: 'three_years', label: '36 Months' },
];

// Default validity days per billing cycle (also enforced as backend minimum)
const CYCLE_DEFAULT_DAYS = {
  monthly: 30, quarterly: 90, half_yearly: 180,
  yearly: 365, two_years: 730, three_years: 1095,
};

export default function Quotations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(null);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [updateResendDialog, setUpdateResendDialog] = useState(null);
  const [updateNote, setUpdateNote] = useState('');
  const [updateUnitPrice, setUpdateUnitPrice] = useState('');
  const [updateValidity, setUpdateValidity] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [viewQuotation, setViewQuotation] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // SP ↔ CP Approval flow state
  const [activeTab, setActiveTab] = useState('my_quotations');
  const [cpPendingQuotations, setCpPendingQuotations] = useState([]);
  const [cpPendingSelfOrders, setCpPendingSelfOrders] = useState([]);
  const [spRejectDialog, setSpRejectDialog] = useState(null);
  const [spRejectReason, setSpRejectReason] = useState('');
  const [spActionLoading, setSpActionLoading] = useState(null);

  // Role helpers
  const isSynergyPartner = user?.role === 'reseller';
  const isChannelPartner = user?.role === 'sub_reseller';

  // Edit dialog for client_requested quotations
  const [editClientReqDialog, setEditClientReqDialog] = useState(null);
  const [editClientReqForm, setEditClientReqForm] = useState({ unit_price: '', billing_cycle: '', validity_days: '', notes: '' });
  const [editClientReqSubmitting, setEditClientReqSubmitting] = useState(false);

  const handleUpdateAndResend = async () => {
    // Validate minimum validity days for the billing cycle
    const minDays = CYCLE_DEFAULT_DAYS[updateResendDialog?.billing_cycle] || 30;
    const enteredDays = parseInt(updateValidity) || 0;
    if (enteredDays < minDays) {
      toast.error(`Minimum validity for ${updateResendDialog?.billing_cycle?.replace('_', ' ')} is ${minDays} days.`);
      return;
    }
    setUpdateSubmitting(true);
    try {
      const payload = { update_note: updateNote };
      // Unit price is locked — cannot be changed after sending to client
      if (updateValidity) payload.validity_days = parseInt(updateValidity);
      await axios.put(`${API}/reseller/quotations/${updateResendDialog.id}/update-and-resend`, payload);
      toast.success('Quotation updated and resent to client');
      setUpdateResendDialog(null);
      setUpdateNote('');
      setUpdateValidity('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update quotation'); }
    finally { setUpdateSubmitting(false); }
  };
  const handleSaveClientRequest = async () => {
    setEditClientReqSubmitting(true);
    try {
      const payload = {};
      if (editClientReqForm.unit_price) payload.unit_price = parseFloat(editClientReqForm.unit_price);
      if (editClientReqForm.billing_cycle) payload.billing_cycle = editClientReqForm.billing_cycle;
      if (editClientReqForm.validity_days) payload.validity_days = parseInt(editClientReqForm.validity_days);
      if (editClientReqForm.notes !== undefined) payload.notes = editClientReqForm.notes;
      await axios.put(`${API}/quotations/${editClientReqDialog.id}`, payload);
      toast.success('Quotation updated');
      setEditClientReqDialog(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setEditClientReqSubmitting(false); }
  };

  const handleViewQuotation = async (quotationId) => {
    setViewLoading(true);
    try {
      const res = await axios.get(`${API}/quotations/${quotationId}`);
      setViewQuotation(res.data);
    } catch { toast.error('Failed to load quotation'); }
    finally { setViewLoading(false); }
  };

  const [newClient, setNewClient] = useState({
    company_name: '',
    email: '',
    password: '',
    phone: '',
    gst_number: '',
    address: ''
  });
  const [addingClient, setAddingClient] = useState(false);
  
  const [newQuotation, setNewQuotation] = useState({
    client_id: '',
    product_id: '',
    billing_cycle: 'monthly',
    quantity: 1,
    unit_price: 0,
    validity_days: CYCLE_DEFAULT_DAYS.monthly,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotationsRes, clientsRes, productsRes, pricingRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/quotations`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/products`),
        axios.get(`${API}/reseller/pricing`),
        axios.get(`${API}/categories`).catch(() => ({ data: [] }))
      ]);
      setQuotations(quotationsRes.data);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      setPricing(pricingRes.data);
      setCategories(categoriesRes.data);

      // Fetch CP pending quotations for Synergy Partners
      if (user?.role === 'reseller') {
        try {
          const cpRes = await axios.get(`${API}/quotations/cp-pending`);
          setCpPendingQuotations(cpRes.data || []);
        } catch { /* silent */ }
        try {
          const soRes = await axios.get(`${API}/partner/cp-self-orders/pending`);
          setCpPendingSelfOrders(soRes.data || []);
        } catch { /* silent */ }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Filter products by selected category
  const filteredProducts = selectedCategory
    ? products.filter(p => p.category === selectedCategory && p.is_active !== false)
    : products.filter(p => p.is_active !== false);

  const getProductPrice = (productId, billingCycle) => {
    const pricingItem = pricing.find(p => p.product?.id === productId);
    if (pricingItem?.custom_pricing?.custom_prices?.add?.[billingCycle]) {
      return pricingItem.custom_pricing.custom_prices.add[billingCycle];
    }
    const product = products.find(p => p.id === productId);
    return product?.billing_prices?.[billingCycle] || product?.base_price || 0;
  };

  const handleAddClient = async () => {
    if (!newClient.company_name || !newClient.email || !newClient.password) {
      toast.error('Please fill in company name, email and password');
      return;
    }

    setAddingClient(true);
    try {
      const response = await axios.post(`${API}/clients`, newClient);
      const addedClient = response.data;
      
      // Add new client to the list and select it
      setClients(prev => [...prev, addedClient]);
      setNewQuotation(prev => ({ ...prev, client_id: addedClient.id }));
      
      // Reset form and close dialog
      setNewClient({
        company_name: '',
        email: '',
        password: '',
        phone: '',
        gst_number: '',
        address: ''
      });
      setAddClientDialogOpen(false);
      toast.success('Client added successfully');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Failed to add client');
      toast.error(errorMessage);
    } finally {
      setAddingClient(false);
    }
  };

  const handleProductChange = (productId) => {
    const selectedProd = products.find(p => p.id === productId);
    // Reset billing cycle to first enabled one for this product
    const enabledCycles = selectedProd?.enabled_billing_cycles && selectedProd.enabled_billing_cycles.length > 0
      ? selectedProd.enabled_billing_cycles
      : BILLING_CYCLES.map(c => c.key);
    const newCycle = enabledCycles.includes(newQuotation.billing_cycle)
      ? newQuotation.billing_cycle
      : (enabledCycles[0] || 'monthly');
    const price = getProductPrice(productId, newCycle);
    setNewQuotation(prev => ({
      ...prev,
      product_id: productId,
      billing_cycle: newCycle,
      unit_price: price,
      validity_days: CYCLE_DEFAULT_DAYS[newCycle] || 30,
    }));
  };

  const handleBillingCycleChange = (cycle) => {
    const price = getProductPrice(newQuotation.product_id, cycle);
    setNewQuotation(prev => ({
      ...prev,
      billing_cycle: cycle,
      unit_price: price,
      // Auto-set validity to the natural period for this cycle
      validity_days: CYCLE_DEFAULT_DAYS[cycle] || 30,
    }));
  };

  const handleCreateQuotation = async () => {
    if (!newQuotation.client_id || !newQuotation.product_id) {
      toast.error('Please select client and product');
      return;
    }

    try {
      await axios.post(`${API}/quotations`, newQuotation);
      toast.success('Quotation created successfully');
      setCreateDialogOpen(false);
      setNewQuotation({
        client_id: '',
        product_id: '',
        billing_cycle: 'monthly',
        quantity: 1,
        unit_price: 0,
        validity_days: CYCLE_DEFAULT_DAYS.monthly,
        notes: ''
      });
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Failed to create quotation');
      toast.error(errorMessage);
    }
  };

  const handleFinalize = async (quotationId) => {
    setFinalizing(quotationId);
    try {
      const res = await axios.post(`${API}/quotations/${quotationId}/finalize`);
      if (res.data?.quotation_status === 'pending_sp_approval') {
        toast.success('Quotation submitted to your Synergy Partner for approval!');
      } else {
        toast.success('Quotation finalized! Order created successfully.');
      }
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Failed to finalize quotation');
      toast.error(errorMessage);
    } finally {
      setFinalizing(null);
    }
  };

  // ── SP Approval Handlers ─────────────────────────────────────────────
  const handleSpApprove = async (quotationId) => {
    setSpActionLoading(quotationId);
    try {
      await axios.post(`${API}/quotations/${quotationId}/sp-approve`);
      toast.success('Quotation approved! Order sent to Admin for activation.');
      setCpPendingQuotations(prev => prev.filter(q => q.id !== quotationId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve quotation');
    } finally {
      setSpActionLoading(null);
    }
  };

  const handleSpApproveSelfOrder = async (selfOrderId) => {
    setSpActionLoading(selfOrderId);
    try {
      await axios.post(`${API}/partner/self-orders/${selfOrderId}/sp-approve`);
      toast.success('Self-order approved! Sent to Admin for provisioning.');
      setCpPendingSelfOrders(prev => prev.filter(o => o.id !== selfOrderId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve self-order');
    } finally {
      setSpActionLoading(null);
    }
  };

  const handleSpReject = async () => {
    if (!spRejectDialog) return;
    if (!spRejectReason.trim()) { toast.error('Please enter a rejection reason'); return; }
    setSpActionLoading(spRejectDialog.id);
    try {
      const isSelfOrder = spRejectDialog._type === 'self_order';
      const endpoint = isSelfOrder
        ? `${API}/partner/self-orders/${spRejectDialog.id}/sp-reject`
        : `${API}/quotations/${spRejectDialog.id}/sp-reject`;
      await axios.post(endpoint, { reason: spRejectReason });
      toast.success(isSelfOrder ? 'Self-order rejected.' : 'Quotation returned to Channel Partner for revision.');
      if (isSelfOrder) {
        setCpPendingSelfOrders(prev => prev.filter(o => o.id !== spRejectDialog.id));
      } else {
        setCpPendingQuotations(prev => prev.filter(q => q.id !== spRejectDialog.id));
      }
      setSpRejectDialog(null);
      setSpRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setSpActionLoading(null);
    }
  };
  // ─────────────────────────────────────────────────────────────────────

  const handleDelete = async (quotationId) => {
    setQuotationToDelete(quotationId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!quotationToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotations/${quotationToDelete}`);
      toast.success('Quotation deleted');
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Failed to delete quotation');
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
    }
  };

  const downloadQuotation = async (quotationId) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}`);
      const { client, reseller, ...quotation } = response.data;
      
      const isFinalized = quotation.status === 'finalized';
      const title = isFinalized ? 'Order Confirmation' : 'Quotation';
      
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title} - ${quotation.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { margin: 0; color: #1e40af; }
            .section { margin-bottom: 25px; }
            .section-title { font-weight: bold; color: #374151; margin-bottom: 10px; font-size: 14px; text-transform: uppercase; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .info-box { background: #f9fafb; padding: 15px; border-radius: 8px; }
            .info-box h3 { margin: 0 0 10px 0; font-size: 14px; color: #6b7280; }
            .info-box p { margin: 5px 0; font-size: 14px; }
            .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .table th, .table td { border: 1px solid #e5e7eb; padding: 12px; text-align: left; }
            .table th { background: #f3f4f6; font-weight: 600; }
            .total-row { font-weight: bold; background: #eff6ff !important; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px; }
            .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .badge-success { background: #dcfce7; color: #166534; }
            .badge-warning { background: #fef3c7; color: #92400e; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${reseller?.company_name || 'CloudSetu'}</h1>
            <p style="color: #6b7280; margin-top: 5px;">${title}</p>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <h3>${title} Details</h3>
              <p><strong>ID:</strong> ${quotation.id.slice(0, 8)}...</p>
              <p><strong>Date:</strong> ${new Date(quotation.created_at).toLocaleDateString()}</p>
              <p><strong>Status:</strong> <span class="badge ${isFinalized ? 'badge-success' : 'badge-warning'}">${isFinalized ? 'Confirmed' : 'Draft'}</span></p>
              ${!isFinalized ? `<p><strong>Valid Until:</strong> ${new Date(quotation.valid_until).toLocaleDateString()}</p>` : ''}
            </div>
            <div class="info-box">
              <h3>Client</h3>
              <p><strong>${client?.company_name || 'N/A'}</strong></p>
              <p>${client?.email || ''}</p>
              <p>${client?.phone || ''}</p>
            </div>
          </div>
          
          <div class="section" style="margin-top: 30px;">
            <div class="section-title">Items</div>
            <table class="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Billing Cycle</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${quotation.product_name}</td>
                  <td>${quotation.billing_cycle.replace('_', ' ').toUpperCase()}</td>
                  <td>${quotation.quantity}</td>
                  <td>₹${(quotation.unit_price || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                  <td>₹${(quotation.subtotal || quotation.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
              </tbody>
            </table>
            <table class="table" style="margin-top:0; border-top:none;">
              <tbody>
                <tr>
                  <td colspan="4" style="text-align:right; border-top:none; color:#6b7280;">Sub Total</td>
                  <td style="border-top:none;">₹${(quotation.subtotal || quotation.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                ${quotation.gst_type === 'CGST+SGST' ? `
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">CGST (9.00%)</td>
                  <td>₹${((quotation.gst_amount || 0) / 2).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">SGST (9.00%)</td>
                  <td>₹${((quotation.gst_amount || 0) / 2).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                ` : `
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">IGST (18.00%)</td>
                  <td>₹${(quotation.gst_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                `}
                <tr class="total-row">
                  <td colspan="4" style="text-align:right;">Grand Total (incl. GST)</td>
                  <td>₹${(quotation.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          ${quotation.notes ? `
          <div class="section">
            <div class="section-title">Notes</div>
            <p style="color: #374151;">${quotation.notes}</p>
          </div>
          ` : ''}
          
          <div class="footer">
            <p>Thank you for your business!</p>
            <p>${reseller?.email || ''} | ${reseller?.phone || ''}</p>
          </div>
        </body>
        </html>
      `;
      
      // Safe print using Blob and iframe instead of document.write (XSS prevention)
      const blob = new Blob([printContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            URL.revokeObjectURL(blobUrl);
          }, 500);
        };
      }
    } catch (error) {
      toast.error('Failed to download quotation');
    }
  };

  const generatePDF = async (data, quotationId) => {
    // Fetch full quotation data for PDF
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}`);
      const quotation = response.data;
      const client = quotation.client;
      
      // Get reseller info from current user context or quotation
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Order - ${data.order_id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { margin: 0; color: #1e40af; }
            .section { margin-bottom: 25px; }
            .section-title { font-weight: bold; color: #374151; margin-bottom: 10px; font-size: 14px; text-transform: uppercase; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .info-box { background: #f9fafb; padding: 15px; border-radius: 8px; }
            .info-box h3 { margin: 0 0 10px 0; font-size: 14px; color: #6b7280; }
            .info-box p { margin: 5px 0; font-size: 14px; }
            .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .table th, .table td { border: 1px solid #e5e7eb; padding: 12px; text-align: left; }
            .table th { background: #f3f4f6; font-weight: 600; }
            .total-row { font-weight: bold; background: #eff6ff !important; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px; }
            .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .badge-success { background: #dcfce7; color: #166534; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>CloudSetu</h1>
            <p style="color: #6b7280; margin-top: 5px;">Order Confirmation</p>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <h3>Order Details</h3>
              <p><strong>Order ID:</strong> ${data.order_id?.slice(0, 8) || 'N/A'}...</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
              <p><strong>Status:</strong> <span class="badge badge-success">Confirmed</span></p>
            </div>
            <div class="info-box">
              <h3>Client</h3>
              <p><strong>${client?.company_name || 'N/A'}</strong></p>
              <p>${client?.email || ''}</p>
              <p>${client?.phone || ''}</p>
            </div>
          </div>
          
          <div class="section" style="margin-top: 30px;">
            <div class="section-title">Order Items</div>
            <table class="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Billing Cycle</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${quotation.product_name}</td>
                  <td>${quotation.billing_cycle?.replace('_', ' ').toUpperCase() || ''}</td>
                  <td>${quotation.quantity}</td>
                  <td>₹${(quotation.unit_price || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                  <td>₹${(quotation.subtotal || quotation.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">Sub Total</td>
                  <td>₹${(quotation.subtotal || quotation.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                ${quotation.gst_type === 'CGST+SGST' ? `
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">CGST (9.00%)</td>
                  <td>₹${((quotation.gst_amount || 0) / 2).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">SGST (9.00%)</td>
                  <td>₹${((quotation.gst_amount || 0) / 2).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                ` : `
                <tr>
                  <td colspan="4" style="text-align:right; color:#6b7280;">IGST (18.00%)</td>
                  <td>₹${(quotation.gst_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
                `}
                <tr class="total-row">
                  <td colspan="4" style="text-align:right;">Grand Total (incl. GST)</td>
                  <td>₹${(quotation.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          ${quotation.notes ? `
          <div class="section">
            <div class="section-title">Notes</div>
            <p style="color: #374151;">${quotation.notes}</p>
          </div>
          ` : ''}
          
          <div class="footer">
            <p>Thank you for your business!</p>
          </div>
        </body>
        </html>
      `;
      
      // Safe print using Blob instead of document.write (XSS prevention)
      const blob = new Blob([printContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            URL.revokeObjectURL(blobUrl);
          }, 500);
        };
      }
    } catch (error) {
      console.log('PDF generation error:', error);
    }
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  const getStatusBadge = (quotation) => {
    if (quotation.status === 'finalized') return <Badge className="bg-green-100 text-green-800">Finalized</Badge>;
    if (quotation.status === 'approved') return <Badge className="bg-blue-100 text-blue-800">Client Approved</Badge>;
    if (quotation.status === 'pending_sp_approval') return <Badge className="bg-amber-100 text-amber-800">Awaiting SP Approval</Badge>;
    if (quotation.status === 'client_requested') return <Badge className="bg-purple-100 text-purple-800 border border-purple-200">Client Requested</Badge>;
    if (quotation.status === 'revision_requested') return <Badge className="bg-amber-100 text-amber-800">Revision Requested</Badge>;
    if (isExpired(quotation.valid_until)) return <Badge variant="destructive">Expired</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800">Pending Client Approval</Badge>;
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-start flex-wrap md-flex-nowrap">
          <div className="mb-3">
            <h1 className="text-3xl font-bold text-slate-800" data-testid="quotations-title">Quotations</h1>
            <p className="text-slate-500 mt-1">Create and manage quotations for your clients</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={fetchData} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 text-slate-500" /></button>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-500 hover:bg-cyan-600" data-testid="create-quotation-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Quotation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Create New Quotation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Client *</Label>
                  </div>
                  <Select value={newQuotation.client_id} onValueChange={(v) => setNewQuotation(prev => ({...prev, client_id: v}))}>
                    <SelectTrigger data-testid="select-client">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={selectedCategory || "all"} 
                    onValueChange={(value) => {
                      setSelectedCategory(value === "all" ? '' : value);
                      setNewQuotation(prev => ({...prev, product_id: '', unit_price: 0}));
                    }}
                  >
                    <SelectTrigger data-testid="select-category">
                      <SelectValue placeholder="Filter by category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id || cat.name} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Product *</Label>
                  <Select value={newQuotation.product_id} onValueChange={handleProductChange}>
                    <SelectTrigger data-testid="select-product">
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredProducts.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} - {product.category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Product Features */}
                {(() => {
                  const sp = products.find(p => p.id === newQuotation.product_id);
                  return sp?.features?.length > 0 ? (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1 mt-2" data-testid="product-features-panel">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Product Features</p>
                      <ul className="space-y-1">
                        {sp.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                            <span className="text-blue-500 mt-0.5">✓</span> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })()}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Billing Cycle</Label>
                    <Select value={newQuotation.billing_cycle} onValueChange={handleBillingCycleChange}>
                      <SelectTrigger data-testid="select-billing-cycle">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const selProd = products.find(p => p.id === newQuotation.product_id);
                          const enabledCycles = selProd?.enabled_billing_cycles && selProd.enabled_billing_cycles.length > 0
                            ? selProd.enabled_billing_cycles
                            : BILLING_CYCLES.map(c => c.key);
                          return BILLING_CYCLES.filter(c => enabledCycles.includes(c.key)).map(cycle => (
                            <SelectItem key={cycle.key} value={cycle.key}>
                              {cycle.label}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newQuotation.quantity}
                      onChange={(e) => setNewQuotation(prev => ({...prev, quantity: parseInt(e.target.value) || 1}))}
                      data-testid="input-quantity"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unit Price (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newQuotation.unit_price}
                      readOnly
                      disabled
                      className="bg-slate-100 cursor-not-allowed"
                      data-testid="input-price"
                    />
                    {newQuotation.unit_price === 0 && newQuotation.product_id && (
                      <p className="text-xs text-amber-600">Please set pricing for this product in the Pricing page</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label>Valid for (days)</Label>
                    <Input
                      type="number"
                      min={CYCLE_DEFAULT_DAYS[newQuotation.billing_cycle] || 30}
                      value={newQuotation.validity_days}
                      onChange={(e) => {
                        const v = parseInt(e.target.value);
                        if (!isNaN(v)) setNewQuotation(prev => ({...prev, validity_days: v}));
                      }}
                      data-testid="input-validity"
                    />
                    <p className="text-xs text-slate-400">
                      Min {CYCLE_DEFAULT_DAYS[newQuotation.billing_cycle] || 30} days for {BILLING_CYCLES.find(c=>c.key===newQuotation.billing_cycle)?.label}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes/Remarks</Label>
                  <Textarea
                    placeholder="Add any notes or special instructions..."
                    value={newQuotation.notes}
                    onChange={(e) => setNewQuotation(prev => ({...prev, notes: e.target.value}))}
                    data-testid="input-notes"
                  />
                </div>

                <div className="bg-[#2563eb]/10 p-4 rounded-lg space-y-1.5 text-sm">
                  {(() => {
                    const subTotal = (newQuotation.unit_price || 0) * (newQuotation.quantity || 1);
                    const gstAmt = Math.round(subTotal * 0.18 * 100) / 100;
                    const total = Math.round((subTotal + gstAmt) * 100) / 100;
                    const selClient = clients.find(c => c.id === newQuotation.client_id);
                    const gstType = getClientGstType(getEntityGstin(selClient), getEntityState(selClient));
                    const half = Math.round(gstAmt / 2 * 100) / 100;
                    const entityState = getEntityState(selClient);
                    const entityGstin = getEntityGstin(selClient);
                    return (
                      <>
                        <div className="flex justify-between"><span className="text-slate-500">Sub Total</span><span className="font-medium">₹{subTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                        {gstType === 'CGST+SGST' ? (
                          <>
                            <div className="flex justify-between"><span className="text-slate-500">CGST (9.00%)</span><span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">SGST (9.00%)</span><span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                          </>
                        ) : (
                          <div className="flex justify-between"><span className="text-slate-500">IGST (18.00%)</span><span className="font-medium">₹{gstAmt.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                        )}
                        <div className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><span className="text-blue-700">₹{total.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateQuotation} data-testid="submit-quotation-btn">
                  Create Quotation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* ── Tabs: SP only ─────────────────────────────────────────────── */}
        {isSynergyPartner && (
          <div className="flex gap-1 border-b border-slate-200 mb-4">
            <button
              data-testid="tab-my-quotations"
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === 'my_quotations' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('my_quotations')}
            >
              My Quotations
            </button>
            <button
              data-testid="tab-pending-approvals"
              className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === 'pending_approvals' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('pending_approvals')}
            >
              Pending from Channel Partners
              {(cpPendingQuotations.length + cpPendingSelfOrders.length) > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5" data-testid="cp-pending-count">
                  {cpPendingQuotations.length + cpPendingSelfOrders.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* ── SP: Pending Approvals Tab ──────────────────────────────────── */}
        {isSynergyPartner && activeTab === 'pending_approvals' && (
          cpPendingQuotations.length === 0 && cpPendingSelfOrders.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No pending approvals</h3>
                <p className="text-slate-500 text-sm mt-1">Channel Partner quotations and self-orders awaiting your approval will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="cp-pending-list">
              {/* ── Pending Quotations ── */}
              {cpPendingQuotations.map(q => (
                <Card key={q.id} data-testid={`cp-pending-card-${q.id}`} className="hover:shadow-md transition-shadow border-l-4 border-l-amber-400">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between flex-wrap sm:flex-nowrap gap-4">
                      <div className="space-y-2 min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-lg">{q.product_name}</h3>
                          <Badge className="bg-amber-100 text-amber-800">Quotation</Badge>
                          {q.channel_partner && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              CP: {q.channel_partner.company_name}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p><span className="text-slate-500">Client:</span> {q.client?.company_name || 'N/A'}</p>
                          <p><span className="text-slate-500">Billing:</span> {BILLING_CYCLES.find(c => c.key === q.billing_cycle)?.label || q.billing_cycle}</p>
                          <p><span className="text-slate-500">Qty:</span> {q.quantity}</p>
                          {q.notes && <p><span className="text-slate-500">Notes:</span> {q.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          <span>Valid until: {new Date(q.valid_until).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}</span>
                          {isExpired(q.valid_until) && <span className="text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Expired</span>}
                        </div>
                      </div>
                      <div className="text-right space-y-3 w-full sm:w-auto">
                        <div>
                          <p className="text-2xl font-bold text-blue-600">₹{(q.sp_price_total ?? q.total_amount)?.toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                          <p className="text-xs text-slate-400 mt-0.5">Your price to CP incl. GST</p>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleSpApprove(q.id)}
                            disabled={!!spActionLoading || isExpired(q.valid_until)}
                            data-testid={`sp-approve-btn-${q.id}`}
                          >
                            {spActionLoading === q.id ? (
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <><Check className="h-4 w-4 mr-1" />Approve</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-600 hover:border-red-600"
                            onClick={() => { setSpRejectDialog({...q, _type: 'quotation'}); setSpRejectReason(''); }}
                            disabled={!!spActionLoading}
                            data-testid={`sp-reject-btn-${q.id}`}
                          >
                            <X className="h-4 w-4 mr-1" />Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* ── Pending Self-Orders ── */}
              {cpPendingSelfOrders.map(so => (
                <Card key={so.id} data-testid={`cp-pending-so-${so.id}`} className="hover:shadow-md transition-shadow border-l-4 border-l-blue-400 mb-2">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between flex-wrap sm:flex-nowrap gap-4">
                      <div className="space-y-2 min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-lg">{so.product_name}</h3>
                          <Badge className="bg-blue-100 text-blue-800">Self-Order</Badge>
                          {so.channel_partner && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              CP: {so.channel_partner.company_name}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p><span className="text-slate-500">Billing:</span> {BILLING_CYCLES.find(c => c.key === so.billing_cycle)?.label || so.billing_cycle}</p>
                          <p><span className="text-slate-500">Qty:</span> {so.quantity}</p>
                          {so.notes && <p><span className="text-slate-500">Notes:</span> {so.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          <span>Submitted: {new Date(so.created_at).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}</span>
                        </div>
                      </div>
                      <div className="text-right space-y-3 w-full sm:w-auto">
                        <div>
                          <p className="text-2xl font-bold text-blue-600">₹{(so.sp_price_total ?? so.total_amount)?.toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                          <p className="text-xs text-slate-400 mt-0.5">Your price to CP incl. GST</p>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleSpApproveSelfOrder(so.id)}
                            disabled={!!spActionLoading}
                            data-testid={`sp-approve-so-btn-${so.id}`}
                          >
                            {spActionLoading === so.id ? (
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <><Check className="h-4 w-4 mr-1" />Approve</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-600 hover:border-red-600"
                            onClick={() => { setSpRejectDialog({...so, _type: 'self_order'}); setSpRejectReason(''); }}
                            disabled={!!spActionLoading}
                            data-testid={`sp-reject-so-btn-${so.id}`}
                          >
                            <X className="h-4 w-4 mr-1" />Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}

        {/* ── My Quotations Tab (CP always, SP on my_quotations tab) ─────── */}
        {(!isSynergyPartner || activeTab === 'my_quotations') && (loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : quotations.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No quotations yet</h3>
              <p className="text-slate-500 text-sm mt-1">Create your first quotation to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 overflow-x-auto">
            {quotations.map(quotation => (
              <Card key={quotation.id} data-testid={`quotation-${quotation.id}`} className="hover:shadow-md transition-shadow mb-2">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between flex-wrap sm:flex-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{quotation.product_name}</h3>
                        {getStatusBadge(quotation)}
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        <p><span className="text-slate-500">Client:</span> {quotation.client?.company_name || 'N/A'} </p>
                        <p><span className="text-slate-500">Billing:</span> {BILLING_CYCLES.find(c => c.key === quotation.billing_cycle)?.label || quotation.billing_cycle}</p>
                        <p><span className="text-slate-500">Qty:</span> {quotation.quantity} × ₹{quotation.unit_price?.toLocaleString()}</p>
                        {quotation.status === 'client_requested' && (
                          <p className="text-purple-600 text-xs font-medium flex items-center gap-1">
                            <span>Client placed this request directly — review, edit if needed, then Finalize to send to Admin</span>
                          </p>
                        )}
                        {quotation.notes && (
                          <p><span className="text-slate-500">Notes:</span> {quotation.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                        <Clock className="h-3 w-3" />
                        <span>Valid until: {new Date(quotation.valid_until).toLocaleDateString()}</span>
                        {quotation.status === 'draft' && isExpired(quotation.valid_until) && (
                          <span className="text-red-500 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Expired
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right space-y-3 w-full sm:w-auto mt-3 sm:mt-0">
                      <div>
                        <p className="text-2xl font-bold text-blue-600 mono">₹{quotation.total_amount?.toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                        <p className="text-xs text-slate-400 mt-0.5">incl. 18% GST</p>
                        <p className="text-xs text-slate-500">Total Amount</p>
                      </div>
                      <div className="flex gap-2">
                        {quotation.status === 'approved' && !isExpired(quotation.valid_until) && (
                          <>
                            {isChannelPartner && quotation.sp_rejection_reason && (
                              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 max-w-xs mb-1 text-left">
                                <strong>SP returned:</strong> {quotation.sp_rejection_reason}
                              </div>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleFinalize(quotation.id)}
                              disabled={finalizing === quotation.id}
                              data-testid={`finalize-${quotation.id}`}
                            >
                              {finalizing === quotation.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                <>
                                  <Check className="h-4 w-4 mr-1" />
                                  Finalize
                                </>
                              )}
                            </Button>
                          </>
                        )}
                        {quotation.status === 'client_requested' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              onClick={() => handleFinalize(quotation.id)}
                              disabled={finalizing === quotation.id}
                              data-testid={`finalize-cr-${quotation.id}`}
                            >
                              {finalizing === quotation.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                <>
                                  <Check className="h-4 w-4 mr-1" />
                                  Finalize
                                </>
                              )}
                            </Button>
                          </>
                        )}
                        {quotation.status === 'draft' && (
                          <span className="text-xs text-slate-500 italic py-2">Awaiting client approval</span>
                        )}
                        {quotation.status === 'pending_sp_approval' && (
                          <span className="text-xs text-amber-600 italic py-2 flex items-center gap-1">
                            <Clock className="h-3 w-3" /> Awaiting SP Approval
                          </span>
                        )}
                        {quotation.status === 'revision_requested' && (
                          <div className="flex items-center gap-2">
                            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 flex-1 max-w-xs">
                              <strong>Client requested:</strong> {quotation.last_revision_request}
                            </div>
                            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                              onClick={() => { setUpdateResendDialog(quotation); setUpdateNote(''); setUpdateUnitPrice(''); setUpdateValidity(String(quotation.validity_days || 30)); }}
                              data-testid={`update-resend-${quotation.id}`}>
                              Update & Resend
                            </Button>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewQuotation(quotation.id)}
                          disabled={viewLoading}
                          data-testid={`view-${quotation.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {(quotation.status === 'draft' || quotation.status === 'client_requested' || quotation.status === 'pending_sp_approval') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(quotation.id)}
                            data-testid={`delete-${quotation.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ))}

        {/* Update & Resend Dialog */}
        <Dialog open={!!updateResendDialog} onOpenChange={(o) => { if (!o) setUpdateResendDialog(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Update Quotation & Resend</DialogTitle>
            </DialogHeader>
            {updateResendDialog && (() => {
              const minDays = CYCLE_DEFAULT_DAYS[updateResendDialog.billing_cycle] || 30;
              const daysError = parseInt(updateValidity) < minDays;
              return (
                <div className="space-y-4 py-2">
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                    <strong>Client requested:</strong> {updateResendDialog.last_revision_request}
                  </div>
                  {/* Unit price is locked — display only */}
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 flex items-center justify-between">
                    <span className="text-sm text-slate-500 flex items-center gap-1.5">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      Unit Price
                    </span>
                    <span className="font-semibold text-slate-700">₹{updateResendDialog.unit_price?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                  </div>
                  {/* Valid For (days) — only editable field */}
                  <div className="space-y-1">
                    <Label>Valid For (days) <span className="text-slate-400 text-xs">— min {minDays} for {updateResendDialog.billing_cycle?.replace('_', ' ')}</span></Label>
                    <Input
                      type="number"
                      min={minDays}
                      value={updateValidity}
                      onChange={e => setUpdateValidity(e.target.value)}
                      className={daysError ? 'border-red-400 focus-visible:ring-red-400' : ''}
                      data-testid="update-validity-input"
                    />
                    {daysError && (
                      <p className="text-xs text-red-500">Minimum {minDays} days required for this billing cycle.</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>Update Note (for client)</Label>
                    <Textarea placeholder="Describe what you changed..." value={updateNote} onChange={e => setUpdateNote(e.target.value)} rows={3} data-testid="update-note-input" />
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setUpdateResendDialog(null)}>Cancel</Button>
              <Button disabled={updateSubmitting} onClick={handleUpdateAndResend} data-testid="confirm-update-resend-btn">
                {updateSubmitting ? 'Updating...' : 'Update & Resend to Client'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Client Request Dialog */}
        <Dialog open={!!editClientReqDialog} onOpenChange={(o) => { if (!o) setEditClientReqDialog(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Client-Requested Quotation</DialogTitle>
            </DialogHeader>
            {editClientReqDialog && (
              <div className="space-y-4 py-2">
                <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm text-purple-800">
                  <strong>Client requested</strong> this order. You can adjust the price, billing cycle and validity before finalizing.
                </div>
                <div className="space-y-1">
                  <Label>Unit Price (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editClientReqForm.unit_price}
                    onChange={e => setEditClientReqForm(f => ({ ...f, unit_price: e.target.value }))}
                    data-testid="edit-cr-price"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Billing Cycle</Label>
                  <Select value={editClientReqForm.billing_cycle} onValueChange={v => setEditClientReqForm(f => ({ ...f, billing_cycle: v }))}>
                    <SelectTrigger data-testid="edit-cr-cycle"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BILLING_CYCLES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Valid For (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editClientReqForm.validity_days}
                    onChange={e => setEditClientReqForm(f => ({ ...f, validity_days: e.target.value }))}
                    data-testid="edit-cr-validity"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Notes</Label>
                  <Textarea
                    placeholder="Optional notes for client..."
                    value={editClientReqForm.notes}
                    onChange={e => setEditClientReqForm(f => ({ ...f, notes: e.target.value }))}
                    rows={3}
                    data-testid="edit-cr-notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditClientReqDialog(null)}>Cancel</Button>
              <Button disabled={editClientReqSubmitting} onClick={handleSaveClientRequest} data-testid="save-cr-btn">
                {editClientReqSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Client Dialog */}
        <Dialog open={addClientDialogOpen} onOpenChange={setAddClientDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  placeholder="Enter company name"
                  value={newClient.company_name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, company_name: e.target.value }))}
                  data-testid="client-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newClient.email}
                  onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="client-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  placeholder="Enter password for client login"
                  value={newClient.password}
                  onChange={(e) => setNewClient(prev => ({ ...prev, password: e.target.value }))}
                  data-testid="client-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  placeholder="10-digit mobile number"
                  value={newClient.phone}
                  maxLength={10}
                  onChange={(e) => setNewClient(prev => ({ ...prev, phone: handleMobileInput(e.target.value) }))}
                  data-testid="client-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input
                  placeholder="22ABCDE1234F1Z5"
                  value={newClient.gst_number}
                  maxLength={15}
                  onChange={(e) => setNewClient(prev => ({ ...prev, gst_number: handleGSTInput(e.target.value) }))}
                  data-testid="client-gst"
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  placeholder="Enter address"
                  value={newClient.address}
                  onChange={(e) => setNewClient(prev => ({ ...prev, address: e.target.value }))}
                  data-testid="client-address"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddClientDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddClient} disabled={addingClient} data-testid="submit-client-btn">
                {addingClient ? 'Adding...' : 'Add Client'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}

        {/* ── Quotation View Modal ───────────────────────────────────────── */}
        <Dialog open={!!viewQuotation} onOpenChange={(o) => { if (!o) setViewQuotation(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {viewQuotation && (() => {
              const { client, reseller, ...q } = viewQuotation;
              const isFinalized = q.status === 'finalized';
              const subTotal   = q.subtotal   ?? (q.unit_price || 0) * (q.quantity || 1);
              const gstAmt     = q.gst_amount ?? Math.round(subTotal * 0.18 * 100) / 100;
              const grandTotal = q.total_amount ?? Math.round((subTotal + gstAmt) * 100) / 100;
              const cycleLabel = BILLING_CYCLES.find(c => c.key === q.billing_cycle)?.label || q.billing_cycle;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-slate-800">
                      <FileText className="h-5 w-5 text-blue-600" />
                      {isFinalized ? 'Order Confirmation' : 'Quotation'} Preview
                    </DialogTitle>
                  </DialogHeader>

                  {/* Status + dates */}
                  <div className="flex flex-wrap gap-2 mt-1">
                    {getStatusBadge(q)}
                    <span className="text-xs text-slate-400 my-auto">
                      Created: {new Date(q.created_at).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}
                    </span>
                    {!isFinalized && q.valid_until && (
                      <span className="text-xs text-slate-400 my-auto">
                        Valid until: {new Date(q.valid_until).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}
                      </span>
                    )}
                  </div>

                  {/* Info grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div className="bg-[#115ab4]/10 rounded-lg p-4 space-y-1 text-sm">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Client</p>
                      <p className="font-semibold text-slate-800">{client?.company_name || '—'}</p>
                      <p className="text-slate-500">{client?.email || '—'}</p>
                      {client?.phone && <p className="text-slate-500">{client.phone}</p>}
                    </div>
                    <div className="bg-[#115ab4]/10 rounded-lg p-4 space-y-1 text-sm">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Issued by</p>
                      <p className="font-semibold text-slate-800">{reseller?.company_name || '—'}</p>
                      <p className="text-slate-500">{reseller?.email || '—'}</p>
                    </div>
                  </div>

                  {/* Items table */}
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Product</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Cycle</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Qty</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Unit Price</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100">
                          <td className="px-4 py-3 font-medium text-slate-800">{q.product_name}</td>
                          <td className="px-4 py-3 text-slate-600">{cycleLabel}</td>
                          <td className="px-4 py-3 text-center text-slate-600">{q.quantity}</td>
                          <td className="px-4 py-3 text-right text-slate-600">₹{(q.unit_price || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                          <td className="px-4 py-3 text-right text-slate-800 font-medium">₹{subTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                        </tr>
                      </tbody>
                    </table>
                    {/* Totals */}
                    <div className="bg-slate-50 px-4 py-3 space-y-1.5 border-t border-slate-200">
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>Sub Total</span>
                        <span>₹{subTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                      </div>
                      {q.gst_type === 'CGST+SGST' ? (
                        <>
                          <div className="flex justify-between text-sm text-slate-500">
                            <span>CGST (9.00%)</span>
                            <span>₹{(gstAmt / 2).toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                          <div className="flex justify-between text-sm text-slate-500">
                            <span>SGST (9.00%)</span>
                            <span>₹{(gstAmt / 2).toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>IGST (18.00%)</span>
                          <span>₹{gstAmt.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-semibold text-slate-800 border-t border-slate-200 pt-1.5 mt-1">
                        <span>Grand Total</span>
                        <span className="text-blue-700">₹{grandTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {q.notes && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-slate-700">
                      <p className="text-xs font-semibold text-amber-700 mb-1 uppercase tracking-wide">Notes</p>
                      {q.notes}
                    </div>
                  )}

                  {/* Revision request if any */}
                  {q.last_revision_request && (
                    <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-sm text-slate-700">
                      <p className="text-xs font-semibold text-orange-700 mb-1 uppercase tracking-wide">Client Revision Request</p>
                      {q.last_revision_request}
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                    <Button variant="outline" onClick={() => setViewQuotation(null)}>Close</Button>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={(o) => { if (!o) setDeleteDialogOpen(false); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Quotation</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600">Are you sure you want to delete this quotation? This action cannot be undone.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* SP Reject / Return Dialog */}
        <Dialog open={!!spRejectDialog} onOpenChange={(o) => { if (!o) setSpRejectDialog(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Return Quotation to Channel Partner</DialogTitle>
            </DialogHeader>
            {spRejectDialog && (
              <div className="space-y-4 py-2">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1">
                  <p><span className="text-slate-500">Product:</span> <strong>{spRejectDialog.product_name}</strong></p>
                  <p><span className="text-slate-500">CP:</span> {spRejectDialog.channel_partner?.company_name || '—'}</p>
                  <p><span className="text-slate-500">Amount:</span> ₹{(spRejectDialog.sp_price_total ?? spRejectDialog.total_amount)?.toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                </div>
                <div className="space-y-1">
                  <Label>Reason for Returning *</Label>
                  <Textarea
                    placeholder="Explain what needs to be revised..."
                    value={spRejectReason}
                    onChange={e => setSpRejectReason(e.target.value)}
                    rows={3}
                    data-testid="sp-reject-reason-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSpRejectDialog(null)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={!spRejectReason.trim() || !!spActionLoading}
                onClick={handleSpReject}
                data-testid="confirm-sp-reject-btn"
              >
                {spActionLoading ? 'Returning...' : 'Return to CP'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
